import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import { createEpicMiddleware, combineEpics } from 'redux-observable';

import globalReducer, { logout, LOGOUT } from './reducer';

// utility epics
import { locallyStoreSessionsEpic } from './auth';
import {
  fetchThemeEpic,
  initializeTenantEpic,
} from '../state/epics/themeRequests';
// end utility epics

// page reducers
import { reducer as loginReducer } from '../pages/login/Login.state';
import {
  FETCH_LOCATIONS_FAILURE,
  reducer as terminalLoginReducer,
} from '../pages/terminal-login/TerminalLogin.state';
import { reducer as checkoutReducer } from '../pages/checkout/Checkout.state';
import { reducer as productsReducer } from '../pages/products/Products.state';
import { reducer as searchReducer } from '../pages/search/Search.state';
import { reducer as lookupReducer } from '../pages/lookup/Lookup.state';
import { reducer as searchResultsReducer } from '../pages/search-results/SearchResults.state';
import { reducer as cartReducer } from '../pages/cart/Cart.state';
import { reducer as orderDetailsReducer } from '../pages/order-details/OrderDetails.state';
import { reducer as ordersIndexReducer } from '../pages/orders-index/OrdersIndex.state';
import { reducer as closeoutReducer } from '../pages/closeout/Closeout.state';
import { reducer as cashDropReducer } from '../pages/cash-drop/CashDrop.state';
import { reducer as cashActionDetailsReducer } from '../pages/cash-action-details/CashActionDetails.state';
import { reducer as drawerHistoryIndexReducer } from '../pages/drawer-history-index/DrawerHistoryIndex.state';
import { reducer as sharedReducer } from './shared';
import { reducer as visitExpirationReducer } from '../pages/cart/VisitExpiration.state';
import { reducer as reportingReducer } from '../pages/reporting/Reporting.state';
import { reducer as paymentDetailsReducer } from '../components/payment-details/PaymentDetails.state';
// end page reducers

import airbrakeMiddleware from '../utils/reduxAirbrakeMiddleware';

// page epics
import { logInOktaUserEpic, logoutEpic } from '../pages/login/Login.epic';
import {
  submitTerminalLoginFormEpic,
  fetchLocationsEpic,
  fetchTerminalsEpic,
} from '../pages/terminal-login/TerminalLogin.epic';
import {
  makePaymentEpic,
  orderStatusResponseEpic,
  cancelTransactionEpic,
  scanCheckEpic,
  checkScanPollingEpic,
  awaitCustomerCreditCardEpic,
  creditCardCountdownEpic,
  getServiceFeeEpic,
  getConvenienceFeeEpic,
  sendPayLinkEpic,
  postVisitIdEpic,
  retryPostVisitIdEpic,
  creditCardReadPollingEpic,
} from '../pages/checkout/Checkout.epic';
import {
  fetchSearchByIdEpic,
  fetchLookupByIdEpic,
  fetchProductsEpic,
  fetchAgenciesEpic,
} from '../pages/products/Products.epics';
import {
  printReceiptEpic,
  returnByIdEpic,
  reopenByIdEpic,
  sendReceiptEpic,
  voidByIdEpic,
  cancelPaylinkByIdEpic,
} from '../pages/order-details/OrderDetails.epic';
import { addToCartEpic } from '../pages/lookup/Lookup.epic';
import {
  getCurrentOrderEpic,
  holdOrderEpic,
  cancelOrderByIdEpic,
  removeItemEpic,
  voidTransactionByIdEpic,
  getUpdatedCartEpic,
} from '../pages/cart/Cart.epic';
import {
  trackVisitExpiresAtEpic,
  visitExpirationWarningTimerEpic,
  visitExpirationCountdownEpic,
  visitExpiredActionEpic,
  clearVisitExpirationOnCartClearEpic,
} from '../pages/cart/VisitExpiration.epic';
import { searchForItemEpic } from '../pages/search/Search.epic';
import { getOrderDetailsEpic } from '../pages/order-details/OrderDetails.epic';
import {
  fetchOrdersEpic,
  fetchBatchesEpic,
  fetchUsersEpic,
  fetchOrdersFilterOptionsEpic,
} from '../pages/orders-index/Orders.epic';
import {
  createCloseoutEpic,
  fetchCurrentBatchEpic,
  fetchCloseoutReportEpic,
} from '../pages/closeout/Closeout.epic';
import { getCashActionDetailsEpic } from '../pages/cash-action-details/CashActionDetails.epics';
import {
  getCloseoutDetailsEpic,
  reprintCloseoutDetailsEpic,
  printReceiptEpic as printCashActionReceiptEpic,
} from '../pages/cash-action-details/CashActionDetails.epics';
import { postCashDropEpic } from '../pages/cash-drop/CashDrop.epics';
import { openCashDrawerEpic } from './epics/peripheralDevicesEpic';
import {
  fetchDrawerHistoriesEpic,
  fetchDrawerHistoriesFilterOptionsEpic,
} from '../pages/drawer-history-index/DrawerHistoryIndex.epics';
import {
  fetchDenominationsEpic,
  fetchCurrentAbilitiesEpic,
  fetchClientConfigEpic,
  resetPinPadEpic,
} from './epics/sharedEpic';
import requestPeriscopeDashboardEpic from '../pages/reporting/Reporting.epic';
import { ENVIRONMENTS, OKTA_LOGOUT_REASON_KEY } from '../utils/constants';
// end page epics

import { Notifier } from '@airbrake/browser';

export const epicMiddleware = createEpicMiddleware();

export const EMPTY_ACTION = { type: 'EMPTY' };

// Allow for multiple enhancers (middleware and dev tools) to be initialized with the store
// https://github.com/zalmoxisus/redux-devtools-extension#12-advanced-store-setup
const composeEnhancers =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        actionsDenylist: [EMPTY_ACTION.type],
      })
    : compose;

const LOGOUT_WORTHY_ACTIONS = [FETCH_LOCATIONS_FAILURE];

const logoutWorthyActionMiddleware = (storeAPI) => (next) => (action) => {
  if (LOGOUT_WORTHY_ACTIONS.includes(action?.type)) {
    // Any logout worthy action must pass navigate from react-router-dom as the LOGOUT action navigates the user to the landing page
    const navigate = action.payload.navigate;
    localStorage.setItem(OKTA_LOGOUT_REASON_KEY, action.payload.err);
    storeAPI.dispatch(logout({ navigate }));
  }
  return next(action);
};

export const getEnhancer = ({ airbrakeErrorMiddleware }) => {
  const middlewares = [epicMiddleware, logoutWorthyActionMiddleware];
  if (airbrakeErrorMiddleware) {
    middlewares.push(airbrakeErrorMiddleware);
  }
  return composeEnhancers(applyMiddleware(...middlewares));
};

const appReducers = {
  global: globalReducer,
  login: loginReducer,
  products: productsReducer,
  search: searchReducer,
  lookup: lookupReducer,
  cart: cartReducer,
  searchResults: searchResultsReducer,
  terminalLogin: terminalLoginReducer,
  checkout: checkoutReducer,
  orderDetails: orderDetailsReducer,
  ordersIndex: ordersIndexReducer,
  closeout: closeoutReducer,
  cashDrop: cashDropReducer,
  cashActionDetails: cashActionDetailsReducer,
  drawerHistoryIndex: drawerHistoryIndexReducer,
  shared: sharedReducer, // intended for state that is used in multiple places throughout the app and doesn't pertain to one single reducer
  visitExpiration: visitExpirationReducer,
  reporting: reportingReducer,
  paymentDetails: paymentDetailsReducer,
  // single token auth system reducer
  config: (defaultConfig = null) => defaultConfig,
};

export const combinedReducer = combineReducers(appReducers);

export const rootReducer = (state, action) => {
  if (action.type === LOGOUT) {
    /* this resets the entire store to its initial state on logout (except for client config).
     other pieces of the state tree can be persisted on logout by appending to the object below.
     this is due to each reducer being undefined except for config, defaulting them to their respective initialStates.
    */
    state = { config: state.config };
  }
  return combinedReducer(state, action);
};

export const rootEpic = combineEpics(
  submitTerminalLoginFormEpic,
  fetchThemeEpic,
  fetchLocationsEpic,
  fetchTerminalsEpic,
  initializeTenantEpic,
  logoutEpic,
  locallyStoreSessionsEpic,
  fetchLookupByIdEpic,
  fetchSearchByIdEpic,
  fetchProductsEpic,
  fetchAgenciesEpic,
  addToCartEpic,
  getCurrentOrderEpic,
  getUpdatedCartEpic,
  holdOrderEpic,
  cancelOrderByIdEpic,
  searchForItemEpic,
  removeItemEpic,
  voidByIdEpic,
  returnByIdEpic,
  reopenByIdEpic,
  getOrderDetailsEpic,
  fetchOrdersEpic,
  makePaymentEpic,
  sendPayLinkEpic,
  printReceiptEpic,
  sendReceiptEpic,
  fetchBatchesEpic,
  fetchUsersEpic,
  postVisitIdEpic,
  retryPostVisitIdEpic,
  orderStatusResponseEpic,
  createCloseoutEpic,
  openCashDrawerEpic,
  fetchCurrentBatchEpic,
  fetchCloseoutReportEpic,
  getCashActionDetailsEpic,
  getCloseoutDetailsEpic,
  postCashDropEpic,
  fetchDrawerHistoriesEpic,
  reprintCloseoutDetailsEpic,
  cancelTransactionEpic,
  printCashActionReceiptEpic,
  fetchDenominationsEpic,
  voidTransactionByIdEpic,
  scanCheckEpic,
  checkScanPollingEpic,
  awaitCustomerCreditCardEpic,
  creditCardCountdownEpic,
  requestPeriscopeDashboardEpic,
  fetchCurrentAbilitiesEpic,
  fetchClientConfigEpic,
  logInOktaUserEpic,
  getServiceFeeEpic,
  getConvenienceFeeEpic,
  fetchOrdersFilterOptionsEpic,
  fetchDrawerHistoriesFilterOptionsEpic,
  resetPinPadEpic,
  cancelPaylinkByIdEpic,
  creditCardReadPollingEpic,
  trackVisitExpiresAtEpic,
  visitExpirationWarningTimerEpic,
  visitExpirationCountdownEpic,
  visitExpiredActionEpic,
  clearVisitExpirationOnCartClearEpic,
);

export const createPosStore = (config) => {
  const AIRBRAKE_EXCLUDED_ENVIRONMENTS = [ENVIRONMENTS.LOCAL];

  let airbrake, airbrakeErrorMiddleware;
  if (!AIRBRAKE_EXCLUDED_ENVIRONMENTS.includes(config.environment)) {
    airbrake = new Notifier({
      projectId: config.airbrakeProjectId,
      projectKey: config.airbrakeProjectKey,
      environment: config.environment,
    });
    airbrakeErrorMiddleware = airbrakeMiddleware(airbrake, {
      noticeAnnotations: { context: { environment: config.environment } },
    });
  }

  const store = createStore(
    rootReducer,
    { config },
    getEnhancer({ airbrakeErrorMiddleware }),
  );
  epicMiddleware.run(rootEpic);

  return store;
};
